import { ChangeEvent, MouseEvent, useCallback, useContext, useEffect, useState } from "react";
import { OrderlineViewDTO, ProjectDTO, ProjectFolderViewDTO } from "../../openapi/requests";
import { OrderlineFileType, OrderStatus } from "../../models/enums";
import { Confirm } from "../layout/Dialog";
import { AuthContext } from "../../contexts/AuthContext";
import { ReactComponent as ExternalLink } from "../../assets/icons/external_link.svg";
import { ReactComponent as Loading } from "../../assets/icons/loading.svg";
import { ReactComponent as Delete } from "../../assets/icons/delete.svg";
import { ReactComponent as Download } from "../../assets/icons/download.svg";
import { useHostedFileServicePostOrderOrderlineHostedFileUploadhostedfile, useOrderlineServicePostOrderlinesApprove, useOrderlineServicePostOrderlinesDelete, useOrderlineServicePostOrderlinesRerender, useOrderlineServicePostOrderOrderlineCompletemanualprocess, useOrderlineServicePostOrderOrderlineRequestManualAdjusments } from "../../openapi/queries";
import ReactGA from "react-ga4";
import { DialogContext } from "../../contexts/DialogContext";
import "./OrderlineListV2.scss";
import { BASE_PATH } from "../..";
import Turntable from "../layout/elements/Turntable";
import { LazyLoadImage } from "react-lazy-load-image-component";
import saveAs from "file-saver";
import format from "date-fns/format";
import { Option } from "../../types/types";
import CheckBox from "../layout/CheckBox";
import { OrderDTO } from "../../openapi/requests";

interface Props {
  project: ProjectDTO | ProjectFolderViewDTO | undefined;
  orderlines: OrderlineViewDTO[];
  orders: OrderDTO[];
  loading: boolean;
  view?: string;
  filter?: Option;
  onUpdate: () => {};
  onMoveToDAM: (orderlines: OrderlineViewDTO[]) => void;
}

export const OrderlineListV2: React.FC<Props> = ({ project, orders, orderlines, view, filter, loading, onUpdate, onMoveToDAM }) => {
  const { hasPermission } = useContext(AuthContext);
  const { message, error } = useContext(DialogContext);
  const [isDownloading, setIsDownLoading] = useState<boolean>(false);
  const [isDownloadingAll, setIsDownLoadingAll] = useState<boolean>(false);
  const [canRerender, setCanRender] = useState<boolean>(false);

  const [internalOrderlines, setInternalOrderlines] = useState<OrderlineViewDTO[]>([]);
  const [selectedOrderlines, setSelectedOrderlines] = useState<OrderlineViewDTO[]>([]);

  const { mutateAsync: requestManualAdjusments } = useOrderlineServicePostOrderOrderlineRequestManualAdjusments();
  const { mutateAsync: deleteOrderlines } = useOrderlineServicePostOrderlinesDelete();
  const { mutateAsync: approveOrderlines } = useOrderlineServicePostOrderlinesApprove();
  const { mutateAsync: uploadHostedFile } = useHostedFileServicePostOrderOrderlineHostedFileUploadhostedfile();
  const { mutateAsync: rerenderOrderlines } = useOrderlineServicePostOrderlinesRerender();
  const { mutateAsync: completeManualProcess } = useOrderlineServicePostOrderOrderlineCompletemanualprocess();

  useEffect(() => {
    let tmpSelectedOrderlines = JSON.parse(JSON.stringify(selectedOrderlines)) as OrderlineViewDTO[];

    const cr = tmpSelectedOrderlines.filter((x) => orders.filter((o) => o.id === x.orderId && (o.status === OrderStatus.Placed || o.status === OrderStatus.Processing || o.status === OrderStatus.Delegating)).length > 0).length === 0;

    setCanRender(cr);
  }, [orders, selectedOrderlines]);

  useEffect(() => {
    setSelectedOrderlines([]);
    setInternalOrderlines([]);

    let lines = orderlines.filter((e) => e.files.length > 0);

    if (!hasPermission("RenderAnimatedCamera")) {
      lines = lines.filter((e) => (e.fileType as unknown as OrderlineFileType) !== OrderlineFileType.Clip);
    }
    if (!hasPermission("RenderVideo")) {
      lines = lines.filter((e) => (e.fileType as unknown as OrderlineFileType) !== OrderlineFileType.Animation);
    }

    if (filter?.value !== "") {
      switch (filter?.value) {
        case "latest":
          {
            const tmpLines: OrderlineViewDTO[] = [];
            lines = lines.filter((e) => !e.workflowUrl && !e.approved && !e.isOnDam);
            const projectIds = orders
              .map((x) => x.projectId)
              .filter((value, index, array) => {
                return array.indexOf(value) === index;
              });
            for (let p in projectIds) {
              const latestProjectOrder = orders
                .filter((x) => x.projectId === projectIds[p] && x.status === 60)
                .sort((a, b) => {
                  return Date.parse(b.timestampCreate) - Date.parse(a.timestampCreate);
                })[0];
              if (latestProjectOrder) {
                const latestOrderline = lines
                  .filter((x) => x.orderId === latestProjectOrder.id)
                  .sort((a, b) => {
                    return Date.parse(b.timestampComplete) - Date.parse(a.timestampComplete);
                  })[0];
                if (latestOrderline) {
                  tmpLines.push(latestOrderline);
                }
              }
            }
            lines = tmpLines;
          }
          break;
        case "preview":
          lines = lines.filter((e) => e.isPreview === true && !e.workflowUrl && !e.approved && !e.isOnDam);
          break;
        case "final":
          lines = lines.filter((e) => e.isPreview === false && !e.workflowUrl && !e.approved && !e.isOnDam);
          break;
        case "adjustment":
          lines = lines.filter((e) => e.workflowUrl && e.status === 20 && !e.approved && !e.isOnDam);
          break;
        case "adjustment_complete":
          lines = lines.filter((e) => e.workflowUrl && e.status === 45 && !e.approved && !e.isOnDam);
          break;
        case "approved":
          lines = lines.filter((e) => e.approved && !e.isOnDam);
          break;
        case "dam":
          lines = lines.filter((e) => e.isOnDam);
          break;
      }
    }

    setInternalOrderlines(lines);
  }, [orderlines, filter, orders, hasPermission]);

  const onRequestManualAdjusments = useCallback(
    (orderline: OrderlineViewDTO) => {
      //  if (order) {
      ReactGA.event("request_manual_adjustments", {});

      requestManualAdjusments({ id: orderline.id, key: orderline.orderId })
        .then((e) => {
          message(
            "Transfer to Workflow successful ✔️",
            <>
              <p>Click the link below to open it in Workflow and start adding your comments.</p>
              <p>
                <a href={e.fullCommentURL} target="_blank" rel="noreferrer">
                  {e.fullCommentURL} <ExternalLink />
                </a>
              </p>
              <p>You can always access the image from this page or directly within Workflow.</p>
              <p>If you’re new to Workflow, we’ve created an account for you. Need help? Feel free to reach out!</p>
            </>
          );
          setInternalOrderlines(internalOrderlines.map((f) => (f.id === orderline.id ? { ...f, workflowUrl: e.fullCommentURL } : f)));

          onUpdate();
        })
        .catch((e) => {
          error("An error occured", e);
        });
      //  }
    },
    [error, internalOrderlines, message, onUpdate, requestManualAdjusments]
  );

  const onDownload = useCallback(
    async (all: boolean, downloadOrderlines: OrderlineViewDTO[]) => {
      if (all) {
        setIsDownLoadingAll(true);
      } else {
        setIsDownLoading(true);
      }

      const token = localStorage.getItem("accessToken");
      ReactGA.event("download_project_files", {});
      let filename = "";

      const isSingleFile = downloadOrderlines.length === 1 && downloadOrderlines[0].files.length === 1;

      let response;
      if (isSingleFile) {
        var orderline = downloadOrderlines[0];
        var file = orderline.files[0];
        response = fetch(`${BASE_PATH}${file.url}`, {
          method: "get",
          headers: {
            Accept: file.contentType,
            "Content-Type": file.contentType,
          },
        });
      } else {
        response = fetch(`${BASE_PATH}/orderlines/files`, {
          method: "post",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify({ name: project?.name, orderlineIds: downloadOrderlines.map((x) => x.id) }),
        });
      }

      response
        .then((res) => {
          const header = res.headers.get("Content-Disposition");
          if (header) {
            const parts = header.split(";");
            filename = parts[1].split("=")[1];
            filename = filename.replaceAll('"', "");
          }
          return res.blob();
        })
        .then((blob) => {
          if (all) {
            setIsDownLoadingAll(false);
          } else {
            setIsDownLoading(false);
          }
          saveAs(blob, filename);
        })
        .catch((reason) => {
          error(undefined, reason);
        });    
    },
    [error, project?.name]
  );

  const onSelect = useCallback(
    (checked: boolean, orderline: OrderlineViewDTO) => {
      let tmpSelectedFiles = JSON.parse(JSON.stringify(selectedOrderlines)) as OrderlineViewDTO[];

      if (checked) {
        tmpSelectedFiles.push(orderline);
      } else {
        tmpSelectedFiles = tmpSelectedFiles.filter((x) => x.id !== orderline.id);
      }

      setSelectedOrderlines(tmpSelectedFiles);
    },
    [selectedOrderlines]
  );

  const onSelectAll = useCallback(
    (e: MouseEvent<HTMLButtonElement>, checked: boolean) => {
      let tmpSelectedOrderlines = JSON.parse(JSON.stringify(selectedOrderlines)) as OrderlineViewDTO[];

      for (let i = 0; i < internalOrderlines.length; i++) {
        const index = tmpSelectedOrderlines.findIndex((x) => x.id === internalOrderlines[i].id);
        if (index >= 0) {
          tmpSelectedOrderlines = tmpSelectedOrderlines.filter((x) => x.id !== internalOrderlines[i].id);
        }
        if (checked) {
          tmpSelectedOrderlines.push(internalOrderlines[i]);
        }
      }

      setSelectedOrderlines(tmpSelectedOrderlines);
    },
    [internalOrderlines, selectedOrderlines]
  );

  const onDeleteOrderlines = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      let tmpSelectedOrderlines = JSON.parse(JSON.stringify(selectedOrderlines)) as OrderlineViewDTO[];
      const orderlineIds = tmpSelectedOrderlines.map((x) => x.id);

      await deleteOrderlines({ requestBody: orderlineIds });
      setSelectedOrderlines([]);

      onUpdate();
    },
    [deleteOrderlines, onUpdate, selectedOrderlines]
  );

  const onDeleteOrderline = useCallback(
    async (orderline: OrderlineViewDTO) => {
      await deleteOrderlines({ requestBody: [orderline.id] });

      setSelectedOrderlines([]);
      onUpdate();
    },
    [deleteOrderlines, onUpdate]
  );

  const onApproveOrderlines = useCallback(
    async (e: MouseEvent<HTMLButtonElement>, approved: boolean) => {
      let tmpSelectedOrderlines = JSON.parse(JSON.stringify(selectedOrderlines)) as OrderlineViewDTO[];
      const orderlineIds = tmpSelectedOrderlines.map((x) => x.id);

      await approveOrderlines({ requestBody: orderlineIds, approved: approved });

      setSelectedOrderlines([]);
      onUpdate();
    },
    [approveOrderlines, onUpdate, selectedOrderlines]
  );

  const onRenderOrderlines = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      let tmpSelectedOrderlines = JSON.parse(JSON.stringify(selectedOrderlines)) as OrderlineViewDTO[];
      const orderlineIds = tmpSelectedOrderlines.map((x) => x.id);

      await rerenderOrderlines({ requestBody: orderlineIds });

      setSelectedOrderlines([]);
      onUpdate();
    },
    [onUpdate, rerenderOrderlines, selectedOrderlines]
  );

  const renderProjectFile = useCallback(
    (index: number, orderline: OrderlineViewDTO, filter: Option | undefined, view: string | undefined) => {
      const fileType: OrderlineFileType = orderline.fileType as unknown as OrderlineFileType;

      let workflowButton: React.ReactNode = null;

      const renderProjectFileImage = (orderline: OrderlineViewDTO, fileType: OrderlineFileType) => {
        switch (fileType) {
          case OrderlineFileType.Spin: {
            return <Turntable sensitivity={0.3} images={orderline.files.map((file) => `${BASE_PATH}${file.url + "/thumbnail/800/png"}`)} />;
          }
          case OrderlineFileType.Clip:
          case OrderlineFileType.Animation: {
            let file = orderline.files[0];
            return <video src={`${BASE_PATH}${file.url}`} controls />;
          }
          case OrderlineFileType.Cutout:
          default: {
            let file = orderline.files[0];
            const rnd = Math.random();
            return <LazyLoadImage src={`${BASE_PATH}${file.url + `/thumbnail/800/png?v=${rnd}`}`} alt={file.name} />;
          }
        }
      };

      if (hasPermission("RequestManualAdjustments")) {
        if (!orderline.approved) {
          if (orderline.workflowUrl) {
            workflowButton = (
              <a className="button rounded" href={orderline.workflowUrl} target="_blank" rel="noreferrer">
                Open in workflow
              </a>
            );
          } else {
            workflowButton = (
              <Confirm title="Request manual adjustment" text="You are now starting a manual adjustment. In order for us to do this, we are transferring the image to our Workflow tool, where you will be able to add your comments. Press 'confirm' to transfer" onConfirm={() => onRequestManualAdjusments(orderline)}>
                <button className="rounded">Request manual adjustment</button>
              </Confirm>
            );
          }
        }
      }

      const getFilterColor = (filter: string | undefined) => {
        switch (filter) {
          case "latest": {
            return { backgroundColor: "rgba(247, 175, 81, 1)", color: "rgba(0,0,0,1)" };
          }
          case "preview": {
            return { backgroundColor: "#3200E6" };
          }
          case "final": {
            return { backgroundColor: "#959AF3" };
          }
          case "adjustment": {
            return { backgroundColor: "#A2A5D3" };
          }
          case "adjustment_complete": {
            return { backgroundColor: "#A2A5D3" };
          }
          case "approved": {
            return { backgroundColor: "#579A1B" };
          }
          case "dam": {
            return { backgroundColor: "#29480E" };
          }
        }
      };

      const onUpload = async (e: ChangeEvent<HTMLInputElement>, orderline: OrderlineViewDTO | undefined) => {
        if (!e.currentTarget.files) {
          return;
        }

        if (!orderline) return;

        var file = e.currentTarget.files[0];
        await uploadHostedFile({
          orderId: orderline.orderId,
          orderlineId: orderline.id,
          formData: { file: file },
        });

        await completeManualProcess({ key: orderline.orderId, id: orderline.id });

        onUpdate();
      };

      return (
        <>
          <div className={`orderlinelistv2-image ${fileType === OrderlineFileType.Spin ? "no-transition" : ""}`}>
            {/* {filter?.value !== "" ? ( */}
            <CheckBox checked={selectedOrderlines.findIndex((ol) => orderline.id === ol.id) >= 0} onChange={(e) => onSelect(e, orderline)}>
              <span>{orderline.status !== 10 ? renderProjectFileImage(orderline, fileType) : <Loading />}</span>
            </CheckBox>
            {/* ) : (
              <span>{(orderline.status !== 10) ? renderProjectFileImage(orderline, fileType) : <Loading />}</span>
            )} */}
          </div>
          <div className="orderlinelistv2-details">
            <div className="orderlinelistv2-filedata">
              <div className="orderlinelistv2-filename">
                <span>{orderline.files[0].name}</span>
              </div>
              <div className="orderlinelistv2-filedate">
                <span>{format(new Date(orderline.files[0].timestampCreate ?? "1900-01-01"), "dd.MM.yyyy - pp")}</span>
              </div>
            </div>
            <div className="orderlinelistv2-fileactions">
                <div className="orderlinelistv2-fileactions-filter">
                  {orderline.isOnDam && <span className="rounded" style={getFilterColor('dam')}>On Dam</span>}
                  {(orderline.approved && !orderline.isOnDam) && <span className="rounded" style={getFilterColor('approved')}>Approved</span>}
                  {(orderline.workflowUrl && orderline.status === 45 && !orderline.approved && !orderline.isOnDam) && <span className="rounded" style={getFilterColor('adjustment_complete')}>Adjustment complete</span>}
                  {(orderline.workflowUrl && orderline.status === 20 && !orderline.approved && !orderline.isOnDam) && <span className="rounded" style={getFilterColor('adjustment')}>In adjustment</span>}
                  {(orderline.isPreview === true && !orderline.workflowUrl && !orderline.approved && !orderline.isOnDam) && <span className="rounded" style={getFilterColor('preview')}>Preview</span>}
                </div>
              <div className="orderlinelistv2-fileactions-actions">
                <div className="orderlinelistv2-fileactions-download">
                  <button onClick={() => onDownload(false, [orderline])}>
                    <Download />
                  </button>
                </div>
                <div className="orderlinelistv2-fileactions-delete">
                  <button onClick={() => onDeleteOrderline(orderline)}>
                    <Delete />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {view === "list" ? (
            <div className="orderlinelistv2-imageactions">
              {workflowButton}
              {filter?.value === "adjustment" && !(fileType === OrderlineFileType.Spin) ? (
                <label className="button rounded">
                  <input type="file" onChange={(e) => onUpload(e, orderline)} />
                  Re-upload Image
                </label>
              ) : null}
            </div>
          ) : null}
        </>
      );
    },
    [completeManualProcess, hasPermission, onDeleteOrderline, onDownload, onRequestManualAdjusments, onSelect, onUpdate, selectedOrderlines, uploadHostedFile]
  );

  return (
    <div className={`orderlinelistv2 ${view}`}>
      {loading && (
        <div className="loading">
          <Loading />
        </div>
      )}
      <ul>
        {/*!loading && (
          <li className="loading">
            <Loading />
          </li>
        )*/}
        {internalOrderlines?.map((file, index) => (
          <li key={file.id}>{renderProjectFile(index, file, filter, view)}</li>
        ))}
        {!loading && internalOrderlines?.length === 0 && (
          <li className="orderlinelistv2-no-images">
            <span>No available images</span>
          </li>
        )}
      </ul>
      <div className="orderlinelistv2-filemultiactions">
        <div>
          <button
            className="rounded"
            disabled={isDownloadingAll || orderlines.length === 0}
            onClick={() => {
              onDownload(true, internalOrderlines);
            }}>
            Download All {isDownloadingAll && <Loading />}
          </button>
          {/* <button className="rounded" disabled={isDownloadingPowerpoint} onClick={onDownloadPowerpoint}>
            Download as powerpoint {isDownloadingPowerpoint && <Loading />}
          </button> */}
        </div>
        {/* {filter?.value !== "" ? ( */}
        <div>
          <button className="link" onClick={(e) => onSelectAll(e, selectedOrderlines.length > 0 ? false : true)}>
            {selectedOrderlines.length > 0 ? "Cancel" : "Select all"}
          </button>
          {selectedOrderlines.length > 0 ? (
            <>
              <button className="rounded-light" onClick={onDeleteOrderlines}>
                Delete
              </button>
              <button
                className="rounded"
                disabled={isDownloading || selectedOrderlines.length === 0}
                onClick={() => {
                  onDownload(false, selectedOrderlines);
                }}>
                Download {isDownloading && <Loading />}
              </button>
              {filter?.value !== "" ? (
                <button disabled={!canRerender} className="rounded" onClick={onRenderOrderlines}>
                  Re-render
                </button>
              ) : null}
              {filter?.value === "approved" ? (
                <button className="rounded" onClick={() => onMoveToDAM(selectedOrderlines)}>
                  Move to Dam
                </button>
              ) : null}
              {filter?.value === "final" || filter?.value === "adjustment_complete" || filter?.value === "approved" || filter?.value === "latest" ? (
                <button className="rounded" onClick={(e) => onApproveOrderlines(e, !(filter.value === "approved"))}>
                  {!(filter.value === "approved") ? "Approve" : "Unapprove"}
                </button>
              ) : null}
            </>
          ) : null}
        </div>
        {/* ) : null} */}
      </div>
    </div>
  );
};
